import { normalizeText } from '@umodeapporg/ui/helpers/stringHelpers';
import type { TFunction } from 'i18next';

/**
 * The manual's SHAPE lives here; every word lives in `ajuda.*` in the locale files, so
 * the text can be edited later without touching a component.
 */

/**
 * The screens, grouped by WHEN they are used instead of by menu order.
 *
 * Fourteen cards with a paragraph each, all looking the same, is a wall: the person who
 * opens the manual to find one screen reads all of them. The moment is what she actually
 * knows ("I am in the middle of the day", "I am promising a date"), so that is what the
 * groups are. `id` doubles as the anchor a link into the manual opens.
 *
 * Ajuda is out on purpose: this IS it, and a card pointing at the page you are on is noise.
 */
const GRUPOS_DE_TELAS = {
  noDiaADia: ['inicio', 'fila', 'apontamento', 'ops'],
  planejar: ['mapa', 'simulador', 'balanceamento', 'portal'],
  acompanhar: ['relatorios', 'curadoria'],
  base: ['cadastros', 'sequencias', 'cronometragem', 'settings'],
} as const;

export type TAjudaGrupo = keyof typeof GRUPOS_DE_TELAS;

/** The groups in reading order: the day first, the setup last. */
export const AJUDA_GRUPOS = Object.keys(GRUPOS_DE_TELAS) as Array<TAjudaGrupo>;

const TELAS = AJUDA_GRUPOS.flatMap((grupo) => GRUPOS_DE_TELAS[grupo]);

export type TAjudaTela = (typeof TELAS)[number];

/** Todas as telas do manual, na ordem de leitura: é por elas que o inventário passa. */
export const AJUDA_TELAS: ReadonlyArray<TAjudaTela> = TELAS;

const PAPEIS = ['renata', 'revisora', 'lider', 'engenharia', 'encarregado', 'ze'] as const;

/**
 * The questions the factory actually asks, in the order the product owner checked them
 * against the system. "How do I" used to be a tab of its own, with the same answers written
 * as tasks: one list, checked once, is worth more than two that can disagree.
 */
const DUVIDAS = [
  'hoje',
  'presenca',
  'apontarHora',
  // Logo depois de "como aponto a hora": é a MESMA pergunta de quem faz jaqueta, e a
  // resposta é justamente que naquela célula não se aponta hora nenhuma.
  'apontarPorEtapa',
  'saldo',
  'conserto',
  'conferirMarca',
  'mpPrevista',
  'cobrarMp',
  'checklist',
  'maquinaParada',
  'balancear',
  'participacao',
  'polivalencia',
  'decidirFila',
  'dataMudou',
  'celulaSugerida',
  // As seis do N74, logo depois de "por que a OP foi parar nesta célula": a pessoa chega
  // por ela e as outras respondem, em ordem, quem decide a célula, o que o sistema nunca
  // mexe, o que é validar, o que o arraste faz e como trocar a célula na mão.
  'modoProgramacao',
  'janelaCongelada',
  'nuncaMoveSozinho',
  'opValidada',
  'arrastarNoMapa',
  'trocarCelula',
  // As seis do N76, na ordem em que a pessoa pergunta: o que o botão faz, o que ele
  // persegue, o que ele nunca mexe, se dá para confiar na proposta, se dá para aceitar
  // só parte dela e como voltar atrás depois de aplicar.
  'botaoOtimizar',
  'objetivoDaOtimizacao',
  'otimizacaoNuncaMexe',
  'propostaEMelhor',
  'aceitarParteDaProposta',
  'desfazerProposta',
  'desfazer',
  'aprovar',
  'simular',
  'importar',
  'devolverDatas',
  'cinza',
  'datasLonge',
  'tempos',
  'entenderNumero',
  'filtrar',
  'portal',
  'feriados',
  'relatorios',
  'faltaValidar',
  'verFicha',
  // Logo depois das outras duas de Sequências: é a mesma tela, e a planilha é por onde
  // entra a sequência de dezenas de produtos de uma vez (N75).
  'subirSequenciaPlanilha',
  // As quatro do ritmo por produto (N73), juntas e nesta ordem: a pessoa chega pela
  // primeira ("onde cadastro") e as outras três respondem o que ela pergunta em seguida.
  'cadastrarProdutos',
  'informarRitmo',
  'ritmoOuTempoInformado',
  'produtividadeSobreRitmo',
] as const;

export type TAjudaDuvidaId = (typeof DUVIDAS)[number];

export interface IAjudaDuvida {
  id: TAjudaDuvidaId;
  pergunta: string;
  resposta: string;
  /** Who asks it and where it gets solved; empty on the questions that have no place. */
  quem: string;
  onde: string;
  /** Everything the search looks at, already normalised: no accent, no case. */
  busca: string;
}

export interface IAjudaTela {
  id: TAjudaTela;
  nome: string;
  rota: string;
  /** Uma linha: para que a tela serve. O texto inteiro fica no "ver detalhes". */
  paraQue: string;
  quemUsa: string;
  texto: string;
}

/** Um grupo de telas, com o título que explica o momento em que elas são usadas. */
export interface IAjudaGrupoDeTelas {
  id: TAjudaGrupo;
  titulo: string;
  telas: Array<IAjudaTela>;
}

export interface IAjudaPapel {
  id: string;
  nome: string;
  quando: string;
  usa: string;
}

function buildTela(t: TFunction, id: TAjudaTela): IAjudaTela {
  return {
    id,
    nome: t(`ajuda.telas.item.${id}.nome`),
    rota: t(`ajuda.telas.item.${id}.rota`),
    paraQue: t(`ajuda.telas.item.${id}.paraQue`),
    quemUsa: t(`ajuda.telas.item.${id}.quemUsa`),
    texto: t(`ajuda.telas.item.${id}.texto`),
  };
}

export function buildTelas(t: TFunction): Array<IAjudaTela> {
  return TELAS.map((id) => buildTela(t, id));
}

/** As telas agrupadas pelo momento em que se usa cada uma, na ordem de leitura. */
export function buildGruposDeTelas(t: TFunction): Array<IAjudaGrupoDeTelas> {
  return AJUDA_GRUPOS.map((id) => ({
    id,
    titulo: t(`ajuda.telas.grupo.${id}`),
    telas: GRUPOS_DE_TELAS[id].map((tela) => buildTela(t, tela)),
  }));
}

export function buildPapeis(t: TFunction): Array<IAjudaPapel> {
  return PAPEIS.map((id) => ({
    id,
    nome: t(`ajuda.papeis.item.${id}.nome`),
    quando: t(`ajuda.papeis.item.${id}.quando`),
    usa: t(`ajuda.papeis.item.${id}.usa`),
  }));
}

export function buildDuvidas(t: TFunction): Array<IAjudaDuvida> {
  return DUVIDAS.map((id) => {
    const pergunta = t(`ajuda.duvidas.item.${id}.pergunta`);
    const resposta = t(`ajuda.duvidas.item.${id}.resposta`);
    const quem = t(`ajuda.duvidas.item.${id}.quem`);
    const onde = t(`ajuda.duvidas.item.${id}.onde`);
    return {
      id,
      pergunta,
      resposta,
      quem,
      onde,
      // Question, answer, who and where: the person searches by any of them.
      busca: normalizeText([pergunta, resposta, quem, onde].join(' ')),
    };
  });
}
